import React from 'react'
import { useLocation } from 'react-router-dom'
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/solid'
import { Button, ButtonText } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import useAppStore from '@/store/useAppStore'
import { BUDGETING_PERIODS } from '@/data/constants/constants'
import { LockBadge, UpgradeModal, useFeatureValue } from '@/components/feature-gate'
import { maxHorizonPeriods } from '@/utils/horizonLimits'
import useCloseToOrigin from '@/hooks/useCloseToOrigin'

/** Forecast horizon picker — shared by GlobalBar and the tablet top bar. */
export default function HorizonMenu() {
  const location = useLocation()
  const numberOfPeriods = useAppStore((state) => state.numberOfPeriods)
  const setNumberOfPeriods = useAppStore((state) => state.setNumberOfPeriods)
  const periodType = useAppStore((state) => state.periodType)
  const setPeriodType = useAppStore((state) => state.setPeriodType)

  const forecastMonths = useFeatureValue('forecast.months', Infinity)
  const maxWeeksForecast = maxHorizonPeriods('Week', forecastMonths)
  const maxMonthsForecast = maxHorizonPeriods('Month', forecastMonths)

  const [horizonUpgradeOpen, setHorizonUpgradeOpen] = React.useState(false)

  const viewSummary = `${numberOfPeriods} ${periodType === BUDGETING_PERIODS.WEEK ? 'weeks' : 'months'}`

  // Changing the horizon used to navigate('/') unconditionally, which threw
  // anyone working in the Table view over to the dashboard. The horizon
  // applies to whichever view you're on, so stay put — and only leave if the
  // bar is being used from on top of a modal route, where staying would mean
  // watching the change happen behind the dialog.
  const isMainView = location.pathname === '/' || location.pathname === '/table'
  const closeToOrigin = useCloseToOrigin()
  const leaveModalIfOpen = () => {
    if (!isMainView) closeToOrigin()
  }

  const onValueChange = (value: string) => {
    const [number, type] = value.split('-')
    setNumberOfPeriods(parseInt(number, 10))
    setPeriodType(type)
    leaveModalIfOpen()
  }

  const renderHorizonItem = (count: number, unit: string, max: number) => {
    const value = `${count}-${unit === 'Weeks' ? BUDGETING_PERIODS.WEEK : BUDGETING_PERIODS.MONTH}`
    const locked = count > max

    if (!locked) {
      return (
        <DropdownMenuRadioItem key={value} value={value}>
          {count} {unit}
        </DropdownMenuRadioItem>
      )
    }

    return (
      <DropdownMenuRadioItem
        key={value}
        value={value}
        className="opacity-50"
        onSelect={(e) => {
          e.preventDefault()
          setHorizonUpgradeOpen(true)
        }}
      >
        <span className="flex flex-1 items-center justify-between gap-2">
          <span>
            {count} {unit}
          </span>
          <LockBadge />
        </span>
      </DropdownMenuRadioItem>
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" dropdown>
            <AdjustmentsHorizontalIcon />
            <ButtonText className="hidden desktop:inline">
              Forecast horizon: {viewSummary}
            </ButtonText>
            <ButtonText className="desktop:hidden">{viewSummary}</ButtonText>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-[220px] justify-start px-2 py-1 text-sm">
          <DropdownMenuLabel>Forecast Horizon</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuLabel>Weeks</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={`${numberOfPeriods}-${periodType}`}
            onValueChange={onValueChange}
          >
            {renderHorizonItem(13, 'Weeks', maxWeeksForecast)}
            {renderHorizonItem(26, 'Weeks', maxWeeksForecast)}
            {renderHorizonItem(52, 'Weeks', maxWeeksForecast)}
          </DropdownMenuRadioGroup>

          <DropdownMenuLabel>Months</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={`${numberOfPeriods}-${periodType}`}
            onValueChange={onValueChange}
          >
            {renderHorizonItem(6, 'Months', maxMonthsForecast)}
            {renderHorizonItem(12, 'Months', maxMonthsForecast)}
            {renderHorizonItem(24, 'Months', maxMonthsForecast)}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <UpgradeModal
        feature={{ lookupKey: 'forecast.months', name: 'Longer forecast horizon' }}
        open={horizonUpgradeOpen}
        onOpenChange={setHorizonUpgradeOpen}
      />
    </>
  )
}
